import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';

const numberFormatter = new Intl.NumberFormat();

export type CollapsedLegendEntry = { key: string; label: string; count: number; custom?: boolean };

/** Marks an agency's own preset so it reads apart from a same-named built-in. */
export const CustomPresetTag = () => (
  <span className="shrink-0 rounded bg-neutral-100 px-1 text-[10px] font-medium text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
    Custom
  </span>
);

const ROW_CLASS = 'flex w-fit max-w-full items-center gap-2.5';

const PANEL_CLASS = clsx(
  'z-20 w-64 max-w-[90vw] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

/** Wraps a legend row and, given `entries`, makes it a button that lists them. */
export const CollapsedLegendPopover = ({
  entries,
  children
}: {
  entries?: CollapsedLegendEntry[];
  children: React.ReactNode;
}) => {
  if (!entries) {
    return <div className={ROW_CLASS}>{children}</div>;
  }

  return (
    <Headless.Popover>
      <Headless.PopoverButton
        className={clsx(
          ROW_CLASS,
          'cursor-pointer rounded text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500'
        )}
      >
        {children}
      </Headless.PopoverButton>
      <Headless.PopoverPanel
        transition
        anchor={{ to: 'bottom start', gap: 4 }}
        className={PANEL_CLASS}
      >
        <ul className="max-h-48 space-y-1 overflow-y-auto overscroll-contain">
          {entries.map((entry) => (
            <li key={entry.key} className="flex items-baseline justify-between gap-3 px-1 text-xs">
              <span className="flex min-w-0 items-baseline gap-1">
                <span className="min-w-0 truncate text-neutral-700 dark:text-neutral-200">
                  {entry.label}
                </span>
                {entry.custom && <CustomPresetTag />}
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-neutral-500 dark:text-neutral-400">
                {numberFormatter.format(entry.count)}
              </span>
            </li>
          ))}
        </ul>
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};
